import { Col, Empty, Flex, Row } from 'antd';
import PropTypes from 'prop-types';
import React from 'react';
import {
  QuestionProcessingType,
  TESelectionType,
  ThemeEnginePanel,
  ThemeSortOptions,
} from '../../../constants';
import {
  displayKeyType,
  exploreThemesSelectionType,
  searchFilterType,
  suggestedGroupType,
  suggestedThemeType,
} from '../../../types';
import { matchesSearch } from '../components/SearchBar';
import ThemeCard from '../components/ThemeCard';
import ThemeGroupCard from '../components/ThemeGroupCard';

const EMPTY_STATE_DESCRIPTIONS = {
  [QuestionProcessingType.AI_GENERATED]: 'There are no more AI generated themes',
  [QuestionProcessingType.REUSE_CODE_FRAME]:
    'No emerging themes detected. Your imported code frame covers all patterns!',
  [QuestionProcessingType.SKIP]:
    'You chose to enter your own code frame, so we skipped AI suggestions.',
};

function SuggestedThemesDisplay({
  phase,
  activeSort,
  selectedSuggestions,
  handleSelection,
  showDescriptions,
  suggestedGroups = undefined,
  suggestedThemes = undefined,
  themeSpan,
  searchFilters,
  displayKey,
  setDisplayKey,
  processingType,
}) {
  const filteredThemes = suggestedThemes.filter((theme) =>
    matchesSearch(`${theme.name} ${theme.description}`, ['themes', 'keywords'], searchFilters),
  );

  const themeCards = filteredThemes.map((t) => ({
    theme: t,
    card: (
      <Col key={t.id} span={themeSpan}>
        <ThemeCard
          panel={ThemeEnginePanel.EXPLORE_THEMES}
          phase={phase}
          theme={t}
          checked={selectedSuggestions[TESelectionType.SUGGESTED_THEME].includes(t.id) || false}
          showDescriptions={showDescriptions}
          handleSelection={handleSelection}
          displayKey={displayKey}
          setDisplayKey={setDisplayKey}
        />
      </Col>
    ),
  }));
  const groupedThemeCardsByGroup = themeCards.reduce((acc, { theme, card }) => {
    if (theme.group_id === null || theme.group_id === undefined) {
      return acc;
    }
    const currentCards = acc.get(theme.group_id) || [];
    acc.set(theme.group_id, [...currentCards, card]);
    return acc;
  }, new Map());
  const ungroupedThemeCards = themeCards
    .filter(({ theme }) => theme.group_id === null || theme.group_id === undefined)
    .map(({ card }) => card);

  const renderContent = () => {
    if (suggestedThemes.length === 0) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={EMPTY_STATE_DESCRIPTIONS[processingType]}
        />
      );
    }
    if (activeSort === ThemeSortOptions.SUGGESTED_GROUPS) {
      const groupedSections = (suggestedGroups || [])
        .filter((g) => groupedThemeCardsByGroup.has(g.id))
        .map((g) => (
          <ThemeGroupCard
            key={g.id}
            phase={phase}
            panel={ThemeEnginePanel.EXPLORE_THEMES}
            group={g}
            themeCards={groupedThemeCardsByGroup.get(g.id)}
            checked={selectedSuggestions[TESelectionType.SUGGESTED_GROUP].includes(g.id) || false}
            handleSelection={handleSelection}
          />
        ));

      if (groupedSections.length === 0) {
        return <Row gutter={[10, 10]}>{ungroupedThemeCards}</Row>;
      }

      return (
        <Flex vertical gap={10}>
          {groupedSections}
          {ungroupedThemeCards.length > 0 && <Row gutter={[10, 10]}>{ungroupedThemeCards}</Row>}
        </Flex>
      );
    }

    const sortedThemeCards = themeCards.sort((a, b) => {
      if (activeSort === ThemeSortOptions.SIZE) {
        return b.theme.response_ids.length - a.theme.response_ids.length;
      }
      return a.theme.id - b.theme.id; // default to unsorted (sort by ID)
    });

    return <Row gutter={[10, 10]}>{sortedThemeCards.map(({ card }) => card)}</Row>;
  };

  return renderContent();
}

SuggestedThemesDisplay.propTypes = {
  phase: PropTypes.string.isRequired,
  activeSort: PropTypes.string.isRequired,
  selectedSuggestions: exploreThemesSelectionType.isRequired,
  handleSelection: PropTypes.func.isRequired,
  showDescriptions: PropTypes.bool.isRequired,
  suggestedGroups: PropTypes.arrayOf(suggestedGroupType),
  suggestedThemes: PropTypes.arrayOf(suggestedThemeType),
  themeSpan: PropTypes.number.isRequired,
  searchFilters: searchFilterType.isRequired,
  displayKey: displayKeyType.isRequired,
  setDisplayKey: PropTypes.func.isRequired,
  processingType: PropTypes.string.isRequired,
};

export default SuggestedThemesDisplay;
